import { getShortformLocale } from '@doss/common-all'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { supportedLocales } from '~/locales'
import {
  ApplicationOrientation,
  Locale,
  useGetUserOrientationQuery,
} from '../generated/apollo-operations'
import { useAuth } from './useAuth'

const ApplicationOrientationContext = createContext({
  orientation: ApplicationOrientation.LEFT_TO_RIGHT,
})

/**
 * Provider that sets the application orientation based on the user's settings.
 * Uses unified auth hook that handles both WorkOS and Firebase (legacy).
 */
export function ApplicationOrientationProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()

  // Unified authentication (handles both WorkOS and Firebase)
  const { isLoading: loadingAuth, isSignedIn: isAuthenticated } = useAuth()

  const { data } = useGetUserOrientationQuery({ skip: !isAuthenticated })

  useEffect(() => {
    if (loadingAuth || !data) {
      return
    }
    const direction =
      data.currentUser.orientation === ApplicationOrientation.RIGHT_TO_LEFT ? 'rtl' : 'ltr'

    document.documentElement.setAttribute('dir', direction)

    // ZH_HK is Traditional Chinese/Cantonese; our 'zh' translations are Simplified Chinese
    // (Mandarin), so ZH_HK must not resolve to the 'zh' bundle like ZH_SG does.
    const shortformLocale =
      data.currentUser.locale && data.currentUser.locale !== Locale.ZH_HK
        ? getShortformLocale(data.currentUser.locale)
        : 'en'
    const locale = supportedLocales.includes(shortformLocale) ? shortformLocale : 'en'
    i18n.changeLanguage(locale)
  }, [data, i18n, loadingAuth])

  const contextValue = useMemo(() => {
    if (!data) {
      return {
        orientation: ApplicationOrientation.LEFT_TO_RIGHT,
      }
    }

    return { orientation: data.currentUser.orientation }
  }, [data])

  return (
    <ApplicationOrientationContext.Provider value={contextValue}>
      {children}
    </ApplicationOrientationContext.Provider>
  )
}

export function useApplicationOrientation() {
  return useContext(ApplicationOrientationContext)
}
